import { FastFieldProps, FormikHandlers } from "formik";
import React, { useState } from "react";
import styled from "styled-components";
import Combobox, { ComboxOptions } from "../combobox/Combobox";
import { ErrorMessage } from "../input/Input";
import Tag from "../tag/Tag";
import { search } from "fast-fuzzy";
import { pxToRem } from "@input-output-hk/px-to-rem";
import { FormFieldLabel } from "./FormFieldLabel";
import { FormFieldElement, FormFieldInputWrapper } from "./FormFieldElement";
import {
  RelatedContentSelector,
  useRelatedContentContext
} from "./CustomMarkdownComponents";

const Tags = styled.div`
  margin-bottom: var(--spacing-xsmall);
  > * + * {
    margin-left: ${pxToRem(8)};
  }
`;

type Props = {
  defaultTags: string[];
  label: string;
  name: string;
  id: string;
  placeholder: string;
  disabled?: boolean;
} & FastFieldProps &
  Pick<FormikHandlers, "getFieldHelpers">;

const rAddTag = /^Add tag: "([^"]+)"$/;

const TagsField: React.FC<Props> = ({
  name,
  defaultTags,
  label,
  placeholder,
  field,
  meta,
  disabled,
  getFieldHelpers,
  ...props
}) => {
  const selectedTags: string[] = field.value;
  const [inputValue, setInputValue] = useState("");
  const { setValue } = getFieldHelpers(name);
  const handleInputChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const inputValue = e.target.value.trim();
    setInputValue(inputValue);
  };
  const toggleTag = (rawTag: string, resetInput = false) => {
    const addTagMatches = rawTag.match(rAddTag);
    const tag = addTagMatches ? addTagMatches[1] : rawTag;
    let nextTags: string[] = selectedTags.slice() || [];
    if (selectedTags.includes(tag)) {
      nextTags = nextTags.filter((t) => t !== tag);
    } else {
      nextTags.push(tag);
    }
    setValue(nextTags);
    if (resetInput) {
      setInputValue("");
    }
  };
  const showError = meta.touched && !!meta.error;
  const availableTags = Array.from(new Set([...defaultTags, ...selectedTags]));
  const hasExactMatch = availableTags.includes(inputValue);
  const searchTags = inputValue
    ? hasExactMatch
      ? search(inputValue, availableTags)
      : [`Add tag: "${inputValue}"`, ...search(inputValue, availableTags)]
    : availableTags;

  const { isSelected, related } = useRelatedContentContext(
    name as RelatedContentSelector
  );

  return (
    <FormFieldElement data-related-selected={isSelected}>
      <FormFieldLabel htmlFor={props.id}>{label}</FormFieldLabel>
      <Tags>
        {selectedTags.map((tag) => {
          return (
            <Tag key={tag} onClickClose={() => toggleTag(tag)} close>
              {tag}
            </Tag>
          );
        })}
      </Tags>
      <FormFieldInputWrapper>
        <Combobox
          name={name}
          onSelect={(tag) => toggleTag(tag, true)}
          openOnFocus
          disabled={disabled}
          value={inputValue}
          onChange={handleInputChange}
          placeholder={placeholder}
          hasError={showError}
        >
          {ComboxOptions(searchTags)}
        </Combobox>
        {related}
      </FormFieldInputWrapper>
      {showError && <ErrorMessage>{meta.error}</ErrorMessage>}
    </FormFieldElement>
  );
};

export default TagsField;
